// Guarded so the element is defined once even if rendered twice.
  if (!customElements.get('pzz-bundle-cart')) {
    const PZZ_CART_UPDATE_EVENT = 'shopify:cart:lines-update'; // StandardEvents.cartLinesUpdate

    class PzzBundleCart extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;

        // Single-instance guard: if the snippet is rendered more than once (e.g.
        // area 'all' AND 'body'), only the first element runs. Two live instances
        // would each insert header rows and observe each other -> flashing.
        if (PzzBundleCart.active && PzzBundleCart.active !== this && document.contains(PzzBundleCart.active)) {
          return;
        }
        PzzBundleCart.active = this;
        this.initialised = true;

        this.modal = this.querySelector('[data-pzz-bundle-modal]');
        this.removeIconHTML = (this.querySelector('[data-pzz-remove-icon]') || {}).innerHTML || '';
        this.currentBundle = null;
        this.running = false;
        this.pending = false;
        this.retries = 0;
        this.debounceTimer = null;
        this.eventsModulePromise = null;
        this.observer = null;         // created lazily — never on an empty load
        this.cartCache = null;        // short-lived /cart.js cache for decoration
        this.cartCacheTime = 0;
        this.TABLE = '.cart-items__table';
        this.ROW = '.cart-items__table-row';

        this.setupModal();

        // The ONLY thing a fresh/empty session attaches. Document-level,
        // event-driven, ~zero idle cost. It's the universal trigger: it fires
        // on every add / update / remove and re-runs decorate() when it matters.
        document.addEventListener(PZZ_CART_UPDATE_EVENT, this.onCartLinesUpdate);

        // Decorate once. The gate inside decorate() returns BEFORE /cart.js and
        // BEFORE any observer when there are no cart rows in the DOM, so a
        // brand-new visitor pays nothing here (no fetch, no MutationObserver on
        // the critical load path). A returning customer's server-rendered
        // cart/drawer rows are still picked up and decorated as before.
        this.decorate();
      }

      disconnectedCallback() {
        document.removeEventListener(PZZ_CART_UPDATE_EVENT, this.onCartLinesUpdate);
        this.teardownObserver();
        if (PzzBundleCart.active === this) PzzBundleCart.active = null;
      }

      // True if any added/removed element node is NOT one of our injected rows.
      // Class/attribute-only and text-only mutations (price shimmer, sticky state,
      // scroll-hint shadows) return false -> no re-decorate -> no flashing.
      isForeignMutation(mutations) {
        for (const m of mutations) {
          const nodes = [];
          m.addedNodes && m.addedNodes.forEach((n) => nodes.push(n));
          m.removedNodes && m.removedNodes.forEach((n) => nodes.push(n));
          for (const n of nodes) {
            if (n.nodeType !== 1) continue;
            if (!(n.matches && n.matches('[data-pzz-bundle-header], [data-pzz-bundle-footer]'))) {
              return true;
            }
          }
        }
        return false;
      }

      // Arrow fn so `this` is bound for add/removeEventListener.
      onCartLinesUpdate = (event) => {
        this.cartCache = null; // cart changed -> next decorate fetches fresh
        this.ensureObserver(); // a mutation is inbound — catch the async row injection
        // Strip our injected rows/classes synchronously, BEFORE the theme morphs,
        // so our extra <tr>s don't fight the section morph. We re-decorate after.
        this.undecorate();
        // The theme handles the SAME event: it morphs the cart/drawer section AND
        // (on add) auto-opens + re-hydrates the drawer. Those can land AFTER a
        // single decorate pass and strip our classes in place (the morph copies
        // attributes onto existing rows, which fires no childList mutation, so the
        // observer can't see it). So we re-assert across a short window; every pass
        // is idempotent, so the one that runs after the morph settles sticks and
        // the rest are free no-ops.
        const reassert = () => this.reassertSoon();
        if (event && event.promise && typeof event.promise.then === 'function') {
          event.promise.then(reassert).catch(reassert);
        } else {
          reassert();
        }
      };

      // Decorate now and again over ~1s to outlast the theme's async morph and
      // the drawer's auto-open re-hydration.
      reassertSoon() {
        this.decorate();
        clearTimeout(this._reassert1);
        clearTimeout(this._reassert2);
        clearTimeout(this._reassert3);
        clearTimeout(this._reassert4);
        this._reassert1 = setTimeout(() => this.decorate(), 120);
        this._reassert2 = setTimeout(() => this.decorate(), 300);
        this._reassert3 = setTimeout(() => this.decorate(), 600);
        this._reassert4 = setTimeout(() => this.decorate(), 1000);
      }

      // Lazily attach the backstop observer — ONLY once a cart mutation has
      // happened or a bundle is already rendered. It watches a STABLE root
      // (<body>) so it's robust against the theme swapping/recreating cart nodes
      // and injecting the drawer table on first add, but it costs nothing the
      // rest of the time because on a fresh/empty session it simply isn't
      // attached. isForeignMutation + idempotent decorate keep each callback
      // cheap. It catches drawer hydration / row injection that the
      // lines-update event alone can miss.
      ensureObserver() {
        if (this.observer) return;
        this.observer = new MutationObserver((mutations) => {
          if (this.isForeignMutation(mutations)) this.scheduleDecorate();
        });
        if (document.body) {
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }

      teardownObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      scheduleDecorate() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.decorate(), 60);
      }

      // Lazily load Shopify's standard-events module (mapped in the importmap as
      // '@shopify/events'). Cached. Dynamic import() honours the importmap even
      // from a classic <script>.
      loadEventsModule() {
        if (!this.eventsModulePromise) {
          this.eventsModulePromise = import('@shopify/events').catch((err) => {
            console.warn('[pzz-bundle-cart] @shopify/events unavailable; will fall back to manual morph.', err);
            return null;
          });
        }
        return this.eventsModulePromise;
      }

      // Section ids of EVERY cart-items-component currently in the DOM (cart page
      // and/or drawer). This is exactly what the theme's own updateQuantity()
      // gathers, so the sections we request match what each component morphs.
      cartSectionIds() {
        const ids = new Set();
        document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
          if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
        });
        return Array.from(ids);
      }

      cartUrl() {
        return (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
      }

      updateUrl() {
        return (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update';
      }

      async fetchCart() {
        const res = await fetch(this.cartUrl() + '.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!res.ok) throw new Error('Could not read cart: ' + res.status);
        return res.json();
      }

      // Cached cart read for decoration only, so a chatty MutationObserver (open
      // drawer) doesn't hammer /cart.js. Busted whenever the cart actually changes.
      async getCartForDecorate() {
        const now = Date.now();
        if (this.cartCache && (now - this.cartCacheTime) < 400) return this.cartCache;
        const cart = await this.fetchCart();
        this.cartCache = cart;
        this.cartCacheTime = now;
        return cart;
      }

      // Group line items by Bundle ID, preserving cart order.
      groupBundles(cart) {
        const bundles = {};
        (cart.items || []).forEach((item) => {
          const props = item.properties || {};
          const id = props['Bundle ID'] || props['_Bundle ID'];
          if (!id) return;
          if (!bundles[id]) {
            bundles[id] = { id: id, keys: [], titles: [], qtys: {}, gift: props['_Gift Packaging'] || 'No' };
          }
          bundles[id].keys.push(item.key);
          bundles[id].titles.push(item.product_title || item.title);
          bundles[id].qtys[item.key] = item.quantity;
        });
        return bundles;
      }

      // The bundles that actually have rows in this tbody, each with its rows in
      // document order. Pure read — no DOM mutation.
      planForTbody(tbody, bundles) {
        const plan = [];
        const ids = Object.keys(bundles);
        if (!ids.length) return plan;

        // Single DOM-order walk: bucket every bundle row by its key → no per-pair
        // compareDocumentPosition (a layout read) inside a sort.
        const allRows = Array.from(tbody.querySelectorAll(this.ROW + '[data-key]'));
        const rowsByBundle = {};
        allRows.forEach((row) => {
          const key = row.getAttribute('data-key');
          ids.forEach((id) => {
            if (bundles[id].keys.indexOf(key) !== -1) {
              (rowsByBundle[id] = rowsByBundle[id] || []).push(row); // already DOM order
            }
          });
        });

        ids.forEach((id) => {
          const rows = rowsByBundle[id];
          if (rows && rows.length) plan.push({ bundle: bundles[id], rows: rows });
        });
        return plan;
      }

      // Signature of the DESIRED decoration (from the cart + current rows).
      planSignature(plan) {
        return plan
          .map((p) =>
            p.bundle.id + '~' + (p.bundle.gift === 'Yes' ? 'g' : '') + '~' +
            p.rows.map((r) => r.getAttribute('data-key')).join(',')
          )
          .join('|');
      }

      // Signature of the decoration ALREADY present in this tbody's DOM.
      domSignature(tbody) {
        const parts = [];
        tbody.querySelectorAll('[data-pzz-bundle-header]').forEach((header) => {
          const id = header.getAttribute('data-pzz-bundle-header');
          const gift = header.querySelector('.bundle-cart__gift-badge') ? 'g' : '';
          const keys = [];
          let n = header.nextElementSibling;
          while (n && !n.hasAttribute('data-pzz-bundle-footer')) {
            if (n.classList.contains('bundle-cart__item') && n.getAttribute('data-pzz-bundle') === id) {
              keys.push(n.getAttribute('data-key'));
            }
            n = n.nextElementSibling;
          }
          parts.push(id + '~' + gift + '~' + keys.join(','));
        });
        return parts.join('|');
      }

      /* -------------------------------------------------------------------
       * Decoration: group line items by Bundle ID into header + frame + footer.
       * IDEMPOTENT — if a tbody's decoration already matches the desired state,
       * it is left completely untouched, so repeated triggers don't rebuild the
       * header row (which would make the remove icon flash and be unclickable).
       * ----------------------------------------------------------------- */
      async decorate() {
        if (this.running) { this.pending = true; return; } // serialise runs
        this.running = true;
        this.pending = false;

        try {
          // ── Fresh-load / empty-cart short-circuit ──────────────────────
          // Bundle decoration only means anything when cart line rows are in
          // the DOM (the /cart table, or a server-rendered / hydrated drawer).
          // On a brand-new session there are none, so bail here BEFORE
          // /cart.js and BEFORE attaching any observer — keeping both off the
          // fresh-load critical path. (The old code fetched /cart.js on every
          // page load even with nothing to decorate.) The lines-update
          // listener AND, once attached, the MutationObserver both re-run
          // decorate() the instant rows appear (add-to-cart), which is the
          // only time the fetch is genuinely needed.
          if (!document.querySelector(this.TABLE + ' ' + this.ROW + '[data-key]')) {
            this.retries = 0;
            return;
          }

          let cart;
          try {
            cart = await this.getCartForDecorate();
          } catch (err) {
            console.error('[pzz-bundle-cart] Could not read cart:', err);
            return;
          }

          const bundles = this.groupBundles(cart);
          const hasBundles = Object.keys(bundles).length > 0;

          // A bundle is present -> make sure the backstop observer is watching.
          // Covers a returning customer landing with a bundle already in their
          // (server-rendered) cart, so a later in-place drawer re-morph is still
          // caught. No bundles -> we leave the observer as-is; it's only ever
          // attached once a mutation happens or a bundle exists, and it tears
          // down on disconnect.
          if (hasBundles) this.ensureObserver();

          const tbodies = Array.from(document.querySelectorAll(this.TABLE + ' tbody'));
          let decoratedAny = false;

          // Observer stays CONNECTED here. Our writes are either filtered out by
          // isForeignMutation (header/footer rows, built off-DOM before insertion)
          // or are attribute-only (the bundle-cart__item class), which childList
          // observation ignores — so we never self-trigger and never miss a
          // concurrent morph.
          tbodies.forEach((tbody) => {
            const plan = this.planForTbody(tbody, bundles);
            if (plan.length) decoratedAny = true;

            // Already correct? Leave it alone — no teardown, no flash.
            if (this.planSignature(plan) === this.domSignature(tbody)) return;

            this.undecorateWithin(tbody);
            plan.forEach((p) => {
              p.rows.forEach((r) => {
                r.classList.add('bundle-cart__item');
                r.setAttribute('data-pzz-bundle', p.bundle.id);
                this.applyQtyMarker(r, p.bundle.qtys[r.getAttribute('data-key')] || 1);
              });
              this.insertHeader(p.bundle, p.rows[0]);
              this.insertFooter(p.bundle, p.rows[p.rows.length - 1]);
            });
          });

          // Mobile race: cart has bundles but their rows aren't in the drawer DOM
          // yet (still rendering). Retry a few times before giving up. (The
          // observer is already attached via ensureObserver above.)
          if (hasBundles && !decoratedAny) {
            if (this.retries < 6) {
              this.retries += 1;
              setTimeout(() => this.decorate(), 150);
            }
          } else {
            this.retries = 0;
          }
        } finally {
          this.running = false;
          if (this.pending) this.scheduleDecorate();
        }
      }

      // Remove all of our injected rows/classes from a single tbody.
      undecorateWithin(tbody) {
        tbody.querySelectorAll('[data-pzz-bundle-header], [data-pzz-bundle-footer]').forEach((n) => n.remove());
        tbody.querySelectorAll('.bundle-cart__item').forEach((r) => {
          r.classList.remove('bundle-cart__item');
          r.removeAttribute('data-pzz-bundle');
        });
        tbody.querySelectorAll('.bundle-cart__qty').forEach((m) => m.remove());
      }

      // Right-aligned ×N marker on the unit-price line. Find-or-create, so
      // repeated decorate passes never duplicate it.
      applyQtyMarker(row, qty) {
        const wrap = row.querySelector('.cart-items__unit-price-wrapper');
        if (!wrap) return;
        let marker = wrap.querySelector('.bundle-cart__qty');
        if (!marker) {
          marker = document.createElement('span');
          marker.className = 'bundle-cart__qty';
          marker.setAttribute('data-pzz-qty', '');
          wrap.appendChild(marker);
        }
        marker.textContent = '×' + qty;
      }

      // Strip our decorations everywhere (used right before the theme morphs the
      // section so we don't interfere with its diff). Observer stays connected —
      // removing our own header/footer rows is filtered by isForeignMutation.
      undecorate() {
        document.querySelectorAll(this.TABLE + ' tbody').forEach((tbody) => this.undecorateWithin(tbody));
      }

      insertHeader(bundle, firstRow) {
        const headerRow = document.createElement('tr');
        headerRow.className = 'bundle-cart__header-row';
        headerRow.setAttribute('data-pzz-bundle-header', bundle.id);

        const cell = document.createElement('td');
        cell.className = 'bundle-cart__header-cell';

        const giftBadge = bundle.gift === 'Yes'
          ? '<span class="bundle-cart__gift-badge">📦 Gift box</span>'
          : '';
        const count = bundle.keys.length;
        const itemWord = count === 1 ? 'item' : 'items';

        cell.innerHTML =
          '<div class="bundle-cart__header-content">' +
            '<div class="bundle-cart__header-left">' +
              '<span class="bundle-cart__id-badge">Bundle: ' + this.escape(bundle.id) + '</span>' +
              giftBadge +
              '<span class="bundle-cart__count">' + count + ' ' + itemWord + '</span>' +
            '</div>' +
            '<button type="button" class="button cart-items__remove bundle-cart__remove-icon" ' +
              'aria-label="Remove Bundle ' + this.escape(bundle.id) + ' from basket">' +
              this.removeIconHTML +
              '<span class="visually-hidden">Remove bundle</span>' +
            '</button>' +
          '</div>';

        headerRow.appendChild(cell);
        firstRow.parentNode.insertBefore(headerRow, firstRow);

        cell.querySelector('.bundle-cart__remove-icon').addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal(bundle);
        });
      }

      insertFooter(bundle, lastRow) {
        const footerRow = document.createElement('tr');
        footerRow.className = 'bundle-cart__footer-row';
        footerRow.setAttribute('data-pzz-bundle-footer', bundle.id);

        const cell = document.createElement('td');
        cell.className = 'bundle-cart__footer-cell';
        footerRow.appendChild(cell);

        if (lastRow.nextSibling) {
          lastRow.parentNode.insertBefore(footerRow, lastRow.nextSibling);
        } else {
          lastRow.parentNode.appendChild(footerRow);
        }
      }

      /* -------------------------------------------------------------------
       * Confirmation modal
       * ----------------------------------------------------------------- */
      setupModal() {
        if (!this.modal) return;
        const cancel = this.modal.querySelector('[data-pzz-modal-cancel]');
        const remove = this.modal.querySelector('[data-pzz-modal-remove]');

        cancel && cancel.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeModal();
        });

        remove && remove.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.currentBundle) this.removeBundle(this.currentBundle.id);
        });

        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });

        this.modal.addEventListener('close', () => {
          document.body.style.overflow = '';
          this.currentBundle = null;
        });
      }

      openModal(bundle) {
        if (!this.modal) return;
        this.currentBundle = bundle;

        const removeBtn = this.modal.querySelector('[data-pzz-modal-remove]');
        if (removeBtn) { removeBtn.textContent = 'Remove bundle'; removeBtn.disabled = false; }

        this.modal.querySelector('[data-pzz-modal-bundle-id]').textContent = bundle.id;

        const items = this.modal.querySelector('[data-pzz-modal-items]');
        items.innerHTML = '<div class="bundle-cart__modal-items-title">This will remove:</div>';
        bundle.titles.forEach((title) => {
          const div = document.createElement('div');
          div.className = 'bundle-cart__modal-item';
          div.textContent = title;
          items.appendChild(div);
        });

        document.body.style.overflow = 'hidden';
        if (typeof this.modal.showModal === 'function') {
          if (!this.modal.open) this.modal.showModal();
        } else {
          this.modal.setAttribute('open', '');
        }

        const cancel = this.modal.querySelector('[data-pzz-modal-cancel]');
        cancel && setTimeout(() => cancel.focus(), 40);
      }

      closeModal() {
        if (!this.modal) return;
        if (typeof this.modal.close === 'function' && this.modal.open) {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
          document.body.style.overflow = '';
          this.currentBundle = null;
        }
      }

      /* -------------------------------------------------------------------
       * Removal — speaks Horizon 4.1's standard-events protocol
       * ----------------------------------------------------------------- */
      async removeBundle(bundleId) {
        const btn = this.modal && this.modal.querySelector('[data-pzz-modal-remove]');
        if (btn) { btn.textContent = 'Removing…'; btn.disabled = true; }
        const restore = () => { if (btn) { btn.textContent = 'Remove bundle'; btn.disabled = false; } };

        try {
          // 1. Resolve this bundle's line keys from the live cart.
          const cart = await this.fetchCart();
          const updates = {};
          const lines = [];
          cart.items.forEach((item) => {
            const props = item.properties || {};
            const id = props['Bundle ID'] || props['_Bundle ID'];
            if (id === bundleId) {
              updates[item.key] = 0;
              lines.push({ id: item.key, quantity: 0 });
            }
          });
          if (!lines.length) throw new Error('Bundle not found in cart');

          // 2. Request the section HTML for every cart-items-component present.
          const sectionIds = this.cartSectionIds();

          // 3. Set up the standard event + deferred promise the theme listens for.
          const events = await this.loadEventsModule();
          let deferred = null;
          if (events && events.CartLinesUpdateEvent) {
            deferred = events.CartLinesUpdateEvent.createPromise();
            document.dispatchEvent(new events.CartLinesUpdateEvent({
              action: 'remove',
              context: 'cart',
              lines: lines,
              promise: deferred.promise
            }));
          }

          // 4. Perform the actual multi-line removal.
          const res = await fetch(this.updateUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              updates: updates,
              sections: sectionIds.join(','),
              sections_url: window.location.pathname
            })
          });
          if (!res.ok) throw new Error('Cart update failed: ' + res.status);
          const updated = await res.json();

          this.closeModal();
          restore();

          // 5. Resolve the promise → cart page, drawer, AND the header count
          //    bubble all re-render themselves off this single payload.
          if (deferred) {
            let cartSummary;
            try {
              cartSummary = events.CartLinesUpdateEvent.createCartFromAjaxResponse
                ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(updated)
                : { totalQuantity: updated.item_count };
            } catch (e) {
              cartSummary = { totalQuantity: updated.item_count };
            }
            deferred.resolve({
              cart: cartSummary,
              detail: {
                sections: updated.sections,
                items: updated.items,
                itemCount: updated.item_count,
                source: 'pzz-bundle-cart',
                didError: false
              }
            });
          } else {
            // Fallback only if the standard-events module wasn't available.
            await this.morphSectionsFallback(updated.sections);
          }

          // 6. Re-group whatever bundles remain (observer also catches the morph).
          this.cartCache = null;
          this.scheduleDecorate();
        } catch (err) {
          console.error('[pzz-bundle-cart] Remove failed:', err);
          restore();
          alert('Sorry, the bundle could not be removed. Please refresh and try again.');
        }
      }

      // Used ONLY when '@shopify/events' can't be imported. Mirrors the theme by
      // morphing each cart-items-component's own section via @theme/section-renderer.
      async morphSectionsFallback(sections) {
        if (!sections) { window.location.reload(); return; }
        try {
          const { morphSection } = await import('@theme/section-renderer');
          document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
            const id = el.dataset.sectionId;
            const html = sections[id];
            if (html != null) {
              morphSection(id, html, { mode: el.dataset.drawer !== undefined ? 'hydration' : 'full' });
            }
          });
        } catch (e) {
          window.location.reload();
        }
      }

      escape(str) {
        return String(str).replace(/[&\u003C\u003E"']/g, (c) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
      }
    }

    customElements.define('pzz-bundle-cart', PzzBundleCart);
  }